/**
 * Referencias de equipo desde `matchDetails` (card).
 * - `teamId` canónico (GN2A1YR66F45G6F / GN2A1YR66F45G6J): identidad real del equipo en live-events.
 * - `code` (MEX/POR): siglas en lineups / liveEvents del mock `view=all`.
 * Las comparaciones priorizan `teamId` GN* y luego `code`.
 */

function trimRef(value) {
  if (value == null) return null;
  const s = String(value).trim();
  return s === '' ? null : s;
}

function normUpper(value) {
  const s = trimRef(value);
  return s != null ? s.toUpperCase() : null;
}

/**
 * @param {object|undefined} md — `matchCard[0].summary.matchDetails`
 * @returns {{ homeTeamId: string|null, awayTeamId: string|null, homeCode: string|null, awayCode: string|null }}
 */
export function getMatchTeamRefs(md) {
  const home = md?.home?.team;
  const away = md?.away?.team;
  return {
    homeTeamId: trimRef(home?.teamId),
    awayTeamId: trimRef(away?.teamId),
    homeCode: trimRef(home?.code),
    awayCode: trimRef(away?.code),
  };
}

/**
 * @param {string|null|undefined} eventTeamId — `teamId` del evento o bloque (p. ej. MEX o GN*)
 * @param {{ homeCode?: string|null, awayCode?: string|null, homeTeamId?: string|null, awayTeamId?: string|null }} refs
 */
export function eventMatchesHome(eventTeamId, refs) {
  const raw = trimRef(eventTeamId);
  if (!raw) return false;

  const homeId = trimRef(refs?.homeTeamId);
  const awayId = trimRef(refs?.awayTeamId);
  if (homeId && raw === homeId) return true;
  if (awayId && raw === awayId) return false;

  const tUpper = normUpper(eventTeamId);
  const homeCode = normUpper(refs?.homeCode);
  const awayCode = normUpper(refs?.awayCode);
  if (homeCode && tUpper === homeCode) return true;
  if (awayCode && tUpper === awayCode) return false;

  return false;
}

/** @param {string|null|undefined} eventTeamId @param {object} refs */
export function eventMatchesAway(eventTeamId, refs) {
  const raw = trimRef(eventTeamId);
  if (!raw) return false;

  const homeId = trimRef(refs?.homeTeamId);
  const awayId = trimRef(refs?.awayTeamId);
  if (awayId && raw === awayId) return true;
  if (homeId && raw === homeId) return false;

  const tUpper = normUpper(eventTeamId);
  const homeCode = normUpper(refs?.homeCode);
  const awayCode = normUpper(refs?.awayCode);
  if (awayCode && tUpper === awayCode) return true;
  if (homeCode && tUpper === homeCode) return false;

  return false;
}

/**
 * `teamId` del evento coincide con local o visitante del `matchCard` (GN* o código MEX/POR).
 * @param {string|null|undefined} eventTeamId
 * @param {ReturnType<typeof getMatchTeamRefs>} refs
 */
export function eventTeamIdBelongsToMatch(eventTeamId, refs) {
  return (
    eventMatchesHome(eventTeamId, refs) || eventMatchesAway(eventTeamId, refs)
  );
}

/** @returns {'left'|'right'|null} — `null` si el `teamId` no pertenece al partido. */
export function resolveEventSide(eventTeamId, refs) {
  if (eventMatchesHome(eventTeamId, refs)) return 'left';
  if (eventMatchesAway(eventTeamId, refs)) return 'right';
  return null;
}

/**
 * @param {object[]} list — `data.lineups` o `data.players`
 * @param {object} refs — `getMatchTeamRefs(md)`
 */
export function resolveLineupSidesByRefs(list, refs) {
  if (!Array.isArray(list) || list.length === 0) {
    return { home: null, away: null };
  }

  const first = list[0];
  if (first?.homeTeam || first?.awayTeam) {
    const homeBundle = Array.isArray(first.homeTeam) ? first.homeTeam[0] : first.homeTeam;
    const awayBundle = Array.isArray(first.awayTeam) ? first.awayTeam[0] : first.awayTeam;
    return {
      home: homeBundle ?? null,
      away: awayBundle ?? null,
    };
  }

  const home =
    list.find((entry) => eventMatchesHome(entry?.teamId, refs)) ?? list[0] ?? null;
  const away =
    list.find((entry) => eventMatchesAway(entry?.teamId, refs)) ??
    list.find((entry) => entry !== home) ??
    list[1] ??
    null;

  return { home, away };
}

export const TEAM_SIDE_LABELS = new Set(['HOME_TEAM', 'AWAY_TEAM']);

/**
 * Relaciona `teamId` del stream (GN*) con el `teamId` del `matchCard` cuando el payload
 * trae `team` + `teamId` juntos (p. ej. HOME_TEAM + GN2A1YR66F45G6F).
 *
 * @param {object[]} streamEvents
 * @param {string|null|undefined} homeTeamId — `matchDetails.home.team.teamId`
 * @param {string|null|undefined} awayTeamId
 * @returns {Map<string, string>}
 */
export function buildStreamTeamIdToCardTeamIdMap(
  streamEvents,
  homeTeamId,
  awayTeamId,
) {
  const map = new Map();
  const sideToCard = {
    HOME_TEAM: homeTeamId != null ? String(homeTeamId).trim() : null,
    AWAY_TEAM: awayTeamId != null ? String(awayTeamId).trim() : null,
  };

  const register = (label, streamId) => {
    if (!label || streamId == null) return;
    const sid = String(streamId).trim();
    if (!sid || TEAM_SIDE_LABELS.has(sid)) return;
    const cardId = sideToCard[label];
    if (cardId) map.set(sid, cardId);
  };

  if (!Array.isArray(streamEvents)) return map;

  streamEvents.forEach((ev) => {
    const p = ev?.payload;
    if (!p) return;
    register(p.team, p.teamId);
    register(p.scoringTeam, p.scoringTeamId);
    register(p.team, p.relatedTeamId);
    register(p.playerOut?.teamLabel, p.playerOut?.teamId);
    register(p.playerIn?.teamLabel, p.playerIn?.teamId);
    register(p.playerOffended?.teamLabel, p.teamId);
    register('HOME_TEAM', p.homeLineup?.teamId);
    register('AWAY_TEAM', p.awayLineup?.teamId);

    const substitutionTeamId = p.substitutionTeamId ?? p.teamId;
    if (substitutionTeamId != null) {
      const sid = String(substitutionTeamId).trim();
      if (homeTeamId && sid === String(homeTeamId).trim()) {
        register('HOME_TEAM', sid);
      } else if (awayTeamId && sid === String(awayTeamId).trim()) {
        register('AWAY_TEAM', sid);
      }
    }

    const scorerTeamId = p.scorerTeamId ?? p.scoringTeamId;
    if (scorerTeamId != null) {
      const sid = String(scorerTeamId).trim();
      if (homeTeamId && sid === String(homeTeamId).trim()) {
        register('HOME_TEAM', sid);
      } else if (awayTeamId && sid === String(awayTeamId).trim()) {
        register('AWAY_TEAM', sid);
      }
    }
  });

  return map;
}

/**
 * Resuelve el bando de un evento por `teamId` GN* (o MEX/POR) y etiqueta HOME/AWAY del stream.
 *
 * @param {string|null|undefined} teamId
 * @param {string|null|undefined} teamLabel — `HOME_TEAM` | `AWAY_TEAM`
 * @param {ReturnType<typeof getMatchTeamRefs>} refs
 * @param {Map<string, string>|undefined} [streamTeamIdMap]
 * @returns {'home'|'away'|null}
 */
export function resolveEventTeamSide(teamId, teamLabel, refs, streamTeamIdMap) {
  if (teamLabel === 'HOME_TEAM') return 'home';
  if (teamLabel === 'AWAY_TEAM') return 'away';

  const idsToCheck = [];
  if (teamId != null) {
    const sid = String(teamId).trim();
    if (sid && !TEAM_SIDE_LABELS.has(sid)) {
      idsToCheck.push(sid);
      const mapped = streamTeamIdMap?.get(sid);
      if (mapped) idsToCheck.push(String(mapped).trim());
    }
  }

  for (const id of idsToCheck) {
    if (eventMatchesHome(id, refs)) return 'home';
    if (eventMatchesAway(id, refs)) return 'away';
  }

  return null;
}
